import { useState, useEffect, useMemo } from "react";
import { format, parseISO, startOfWeek, endOfWeek, addWeeks, subWeeks, eachDayOfInterval, isSameWeek } from "date-fns";
import { fr } from "date-fns/locale";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "../ui/drawer";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { ScrollArea } from "../ui/scroll-area";
import { Progress } from "../ui/progress";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  Clock,
  BarChart3,
  AlertTriangle,
  CheckCircle2,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import { getMatiereInfo } from "./constants";
import { getSessionsForPeriod, getOfficialHours } from "../../lib/api";

// Official hours by cycle (hours per week)
const OFFICIAL_HOURS = {
  cycle1: {
    "Mobiliser le langage": 6,
    "Agir, s'exprimer, comprendre à travers l'activité physique": 3,
    "Agir, s'exprimer, comprendre à travers les activités artistiques": 3,
    "Construire les premiers outils pour structurer sa pensée": 4.5,
    "Explorer le monde": 3,
  },
  cycle2: {
    "Français": 10,
    "Mathématiques": 5,
    "Langue vivante": 1.5,
    "Éducation physique et sportive": 3,
    "Enseignements artistiques": 2,
    "Questionner le monde": 2.5,
    "Enseignement moral et civique": 0.6,
  },
  cycle3: {
    "Français": 8,
    "Mathématiques": 5,
    "Langue vivante": 1.5,
    "Éducation physique et sportive": 3,
    "Enseignements artistiques": 2,
    "Sciences et technologie": 2,
    "Histoire et géographie": 2.5,
    "Enseignement moral et civique": 0.6,
  },
};

// Period options
const PERIOD_OPTIONS = [
  { value: 1, label: "1 semaine" },
  { value: 2, label: "2 semaines" },
  { value: 3, label: "3 semaines" },
  { value: 4, label: "4 semaines (1 mois)" },
  { value: 5, label: "5 semaines (Période)" },
  { value: 6, label: "6 semaines" },
];

function SubjectRow({ subject, planned, official, weekCount }) {
  const matiereInfo = getMatiereInfo(subject);
  const totalOfficial = official * weekCount;
  const percentage = totalOfficial > 0 ? Math.round((planned / totalOfficial) * 100) : 0;
  const diff = planned - totalOfficial;
  const diffHours = Math.abs(diff).toFixed(1);
  
  let status = "ok";
  if (percentage < 80) status = "low";
  else if (percentage > 120) status = "high";
  
  const StatusIcon = status === "low" ? TrendingDown : status === "high" ? TrendingUp : Minus;
  const statusColor = status === "low" ? "text-red-500" : status === "high" ? "text-amber-500" : "text-green-500";
  const bgColor = status === "low" ? "bg-red-50" : status === "high" ? "bg-amber-50" : "bg-green-50";

  return (
    <div className={`p-3 rounded-lg ${bgColor} mb-2`}>
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <div
            className="w-3 h-3 rounded-full"
            style={{ backgroundColor: matiereInfo?.color || "#6366f1" }}
          />
          <span className="font-medium text-sm text-slate-700">{subject}</span>
        </div>
        <div className="flex items-center gap-2">
          <StatusIcon className={`w-4 h-4 ${statusColor}`} />
          <Badge variant="outline" className={`text-xs ${statusColor}`}>
            {diff >= 0 ? "+" : ""}{diffHours}h
          </Badge>
        </div>
      </div>
      
      <div className="flex items-center gap-3">
        <Progress 
          value={Math.min(percentage, 100)} 
          className="flex-1 h-2"
        />
        <span className="text-xs text-slate-500 min-w-[80px] text-right">
          {planned.toFixed(1)}h / {totalOfficial.toFixed(1)}h
        </span>
      </div>
      
      <div className="flex justify-between mt-1 text-xs text-slate-400">
        <span>{percentage}% du temps prévu</span>
        <span>{(planned / weekCount).toFixed(1)}h/sem moy.</span>
      </div>
    </div>
  );
}

function PeriodSummaryContent({ 
  startDate, 
  weekCount, 
  sessions, 
  cycle,
  onPrevPeriod,
  onNextPeriod,
  onWeekCountChange,
}) {
  // Calculate hours by subject
  const hoursBySubject = useMemo(() => {
    const hours = {};
    
    sessions.forEach(session => {
      const subject = session.matiere_label || session.matiere || "Autre";
      const startTime = session.start_time;
      const endTime = session.end_time;
      
      if (startTime && endTime) {
        const [startH, startM] = startTime.split(":").map(Number);
        const [endH, endM] = endTime.split(":").map(Number);
        const duration = (endH * 60 + endM - startH * 60 - startM) / 60;
        
        hours[subject] = (hours[subject] || 0) + Math.max(0, duration);
      } else {
        // Default 45 minutes if no time
        hours[subject] = (hours[subject] || 0) + 0.75;
      }
    });
    
    return hours;
  }, [sessions]);

  // Get official hours for the cycle
  const officialHours = OFFICIAL_HOURS[cycle] || OFFICIAL_HOURS.cycle2;

  // Calculate totals
  const totalPlanned = Object.values(hoursBySubject).reduce((a, b) => a + b, 0);
  const totalOfficial = Object.values(officialHours).reduce((a, b) => a + b, 0) * weekCount;

  // Period date range
  const endDate = addWeeks(startDate, weekCount - 1);
  const endOfPeriod = endOfWeek(endDate, { locale: fr, weekStartsOn: 1 });

  // Merge subjects from both planned and official
  const allSubjects = [...new Set([
    ...Object.keys(hoursBySubject),
    ...Object.keys(officialHours)
  ])];

  return (
    <div className="space-y-4">
      {/* Period Navigation */}
      <div className="flex items-center justify-between">
        <Button variant="ghost" size="sm" onClick={onPrevPeriod}>
          <ChevronLeft className="w-4 h-4" />
        </Button>
        
        <div className="text-center">
          <p className="font-semibold text-slate-800">
            {format(startDate, "d MMM", { locale: fr })} - {format(endOfPeriod, "d MMM yyyy", { locale: fr })}
          </p>
          <p className="text-xs text-slate-500">{weekCount} semaine(s)</p>
        </div>
        
        <Button variant="ghost" size="sm" onClick={onNextPeriod}>
          <ChevronRight className="w-4 h-4" />
        </Button>
      </div>

      {/* Period Length Selector */}
      <div className="flex items-center gap-2">
        <Calendar className="w-4 h-4 text-slate-400" />
        <Select value={weekCount.toString()} onValueChange={(v) => onWeekCountChange(parseInt(v))}>
          <SelectTrigger className="flex-1 h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {PERIOD_OPTIONS.map((opt) => (
              <SelectItem key={opt.value} value={opt.value.toString()}>
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-2">
        <Card className="bg-indigo-50 border-indigo-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-indigo-700">{sessions.length}</p>
            <p className="text-xs text-indigo-600">Séances</p>
          </CardContent>
        </Card>
        <Card className="bg-emerald-50 border-emerald-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-emerald-700">{totalPlanned.toFixed(1)}h</p>
            <p className="text-xs text-emerald-600">Planifiées</p>
          </CardContent>
        </Card>
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-3 text-center">
            <p className="text-2xl font-bold text-amber-700">{totalOfficial.toFixed(1)}h</p>
            <p className="text-xs text-amber-600">Officielles</p>
          </CardContent>
        </Card>
      </div>

      {/* Progress Bar */}
      <div className="p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-slate-700">Progression globale</span>
          <span className="text-sm text-slate-500">
            {totalOfficial > 0 ? Math.round((totalPlanned / totalOfficial) * 100) : 0}%
          </span>
        </div>
        <Progress 
          value={totalOfficial > 0 ? Math.min((totalPlanned / totalOfficial) * 100, 100) : 0} 
          className="h-3"
        />
      </div>

      {/* Subject Breakdown */}
      <div>
        <h4 className="font-semibold text-slate-700 mb-3 flex items-center gap-2">
          <BarChart3 className="w-4 h-4" />
          Répartition par matière
        </h4>
        
        <ScrollArea className="h-[300px] pr-2">
          {allSubjects.sort().map((subject) => (
            <SubjectRow
              key={subject}
              subject={subject}
              planned={hoursBySubject[subject] || 0}
              official={officialHours[subject] || 0}
              weekCount={weekCount}
            />
          ))}
        </ScrollArea>
      </div>

      {/* Legend */}
      <div className="flex items-center justify-center gap-4 text-xs text-slate-500 pt-2 border-t">
        <div className="flex items-center gap-1">
          <TrendingDown className="w-3 h-3 text-red-500" />
          <span>&lt;80% (insuffisant)</span>
        </div>
        <div className="flex items-center gap-1">
          <Minus className="w-3 h-3 text-green-500" />
          <span>80-120% (correct)</span>
        </div>
        <div className="flex items-center gap-1">
          <TrendingUp className="w-3 h-3 text-amber-500" />
          <span>&gt;120% (excès)</span>
        </div>
      </div>
    </div>
  );
}

export function PeriodSummaryDialog({ open, onOpenChange, cycle = "cycle2" }) {
  const [isMobile, setIsMobile] = useState(false);
  const [startDate, setStartDate] = useState(() => startOfWeek(new Date(), { locale: fr, weekStartsOn: 1 }));
  const [weekCount, setWeekCount] = useState(5); // Default: 5 weeks (période)
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  // Fetch sessions for the period
  useEffect(() => {
    if (!open) return;
    
    const fetchSessions = async () => {
      setLoading(true);
      try {
        const endDate = addWeeks(startDate, weekCount);
        const startStr = format(startDate, "yyyy-MM-dd");
        const endStr = format(endDate, "yyyy-MM-dd");
        
        const { data } = await getSessionsForPeriod(startStr, endStr);
        setSessions(data?.sessions || []);
      } catch (err) {
        console.error("Error fetching period sessions:", err);
        setSessions([]);
      } finally {
        setLoading(false);
      }
    };
    
    fetchSessions();
  }, [open, startDate, weekCount]);

  const handlePrevPeriod = () => {
    setStartDate(prev => subWeeks(prev, weekCount));
  };

  const handleNextPeriod = () => {
    setStartDate(prev => addWeeks(prev, weekCount));
  };

  const content = loading ? (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
    </div>
  ) : (
    <PeriodSummaryContent
      startDate={startDate}
      weekCount={weekCount}
      sessions={sessions}
      cycle={cycle}
      onPrevPeriod={handlePrevPeriod}
      onNextPeriod={handleNextPeriod}
      onWeekCountChange={setWeekCount}
    />
  );

  if (isMobile) {
    return (
      <Drawer open={open} onOpenChange={onOpenChange}>
        <DrawerContent className="max-h-[90vh]">
          <DrawerHeader>
            <DrawerTitle className="flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-indigo-600" />
              Vue Période
            </DrawerTitle>
          </DrawerHeader>
          <div className="px-4 pb-6 overflow-y-auto">
            {content}
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-indigo-600" />
            Vue Période - Synthèse horaire
          </DialogTitle>
        </DialogHeader>
        <ScrollArea className="flex-1 pr-4">
          {content}
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}

export default PeriodSummaryDialog;
